/**
 * FirstProductStep - Design System v2
 * Matching Login page aesthetic with glassmorphism
 * OPTIONNEL - Premier produit/service
 */

import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ShoppingBag, ArrowRight, Package, SkipForward } from 'lucide-react';
import { productsAPI } from '@/utils/productsAPI';
import { taxRatesAPI, type TaxRate } from '@/utils/taxRatesAPI';
import { logger } from '@/utils/logger';

interface FirstProductStepProps {
  onComplete: () => void;
  onSkip: () => void;
}

// Glass Input component
const GlassInput: React.FC<{
  label: string;
  value: string | number;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur?: () => void;
  placeholder?: string;
  required?: boolean;
  type?: string;
  error?: string;
  step?: number;
  min?: number;
  className?: string;
}> = ({ label, value, onChange, onBlur, placeholder, required, type = 'text', error, step, min, className = '' }) => (
  <div className={className}>
    <label className="block text-sm font-medium text-white/80 mb-2">
      {label} {required && <span className="text-lime-400">*</span>}
    </label>
    <input
      type={type}
      value={value}
      onChange={onChange}
      onBlur={onBlur}
      step={step}
      min={min}
      className={`w-full px-4 py-3 bg-white/10 backdrop-blur-md border rounded-2xl text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-lime-400/50 focus:border-lime-400/50 transition-all duration-300 ${
        error ? 'border-red-400/50' : 'border-white/20'
      }`}
      placeholder={placeholder}
    />
    {error && <p className="mt-1 text-sm text-red-400">{error}</p>}
  </div>
);

// Glass Select component
const GlassSelect: React.FC<{
  label: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  options: { value: string; label: string }[];
  required?: boolean;
  disabled?: boolean;
  className?: string;
}> = ({ label, value, onChange, options, required, disabled, className = '' }) => (
  <div className={className}>
    <label className="block text-sm font-medium text-white/80 mb-2">
      {label} {required && <span className="text-lime-400">*</span>}
    </label>
    <select
      value={value}
      onChange={onChange}
      disabled={disabled}
      className="w-full px-4 py-3 bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl text-white focus:outline-none focus:ring-2 focus:ring-lime-400/50 focus:border-lime-400/50 transition-all duration-300 disabled:opacity-50"
    >
      {options.map((opt) => (
        <option key={opt.value} value={opt.value} className="bg-teal-800 text-white">
          {opt.label}
        </option>
      ))}
    </select>
  </div>
);

// Glass Button component
const GlassButton: React.FC<{
  children: React.ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  variant?: 'primary' | 'secondary' | 'ghost';
  isLoading?: boolean;
  className?: string;
  type?: 'button' | 'submit';
}> = ({ children, onClick, disabled, variant = 'primary', isLoading, className = '', type = 'button' }) => (
  <button
    type={type}
    onClick={onClick}
    disabled={disabled || isLoading}
    className={`
      flex items-center justify-center gap-2 px-6 py-3 rounded-2xl font-semibold transition-all duration-300
      ${variant === 'primary'
        ? 'bg-gradient-to-r from-lime-400 to-lime-500 text-teal-800 hover:shadow-lg hover:shadow-lime-500/30 hover:-translate-y-0.5 disabled:opacity-50'
        : variant === 'ghost'
        ? 'text-white/60 hover:text-white hover:bg-white/10'
        : 'bg-white/10 backdrop-blur-md border border-white/20 text-white hover:bg-white/20 hover:border-white/30'
      }
      ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
      ${className}
    `}
  >
    {isLoading ? (
      <div className="animate-spin rounded-full h-5 w-5 border-2 border-current border-t-transparent" />
    ) : children}
  </button>
);

export const FirstProductStep: React.FC<FirstProductStepProps> = ({ onComplete, onSkip }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    unitPrice: '',
    unit: 'unité',
    categoryId: '',
    taxRateId: '',
  });
  const [taxRates, setTaxRates] = useState<TaxRate[]>([]);
  const [isLoadingTaxRates, setIsLoadingTaxRates] = useState(true);
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadTaxRates = async () => {
      try {
        setIsLoadingTaxRates(true);
        const taxRatesRes = await taxRatesAPI.getAll({ activeOnly: true });
        setTaxRates(taxRatesRes.taxRates || []);

        const defaultRate = taxRatesRes.taxRates?.find((tr: TaxRate) => tr.isDefault || Number(tr.rate) === 20) || taxRatesRes.taxRates?.[0];
        if (defaultRate) {
          setFormData(prev => ({ ...prev, taxRateId: defaultRate.id }));
        }
      } catch (err: any) {
        logger.error('❌ Erreur chargement taux TVA:', err);
      } finally {
        setIsLoadingTaxRates(false);
      }
    };

    loadTaxRates();
  }, []);

  const handleChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleBlur = (field: string) => {
    setTouched(prev => ({ ...prev, [field]: true }));
  };

  const getFieldError = (field: string): string | undefined => {
    if (!touched[field]) return undefined;

    if (field === 'name' && !formData.name.trim()) {
      return t('onboarding.product.required');
    }
    if (field === 'unitPrice') {
      if (!formData.unitPrice) {
        return t('onboarding.product.required');
      }
      const price = parseFloat(formData.unitPrice);
      if (isNaN(price) || price < 0) {
        return t('onboarding.product.invalidPrice');
      }
    }

    return undefined;
  };

  const handleSubmit = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    setError(null);

    if (!formData.name || !formData.unitPrice) {
      setError(t('onboarding.product.nameAndPriceRequired'));
      return;
    }

    if (!formData.taxRateId) {
      setError(t('onboarding.product.vatRequired'));
      return;
    }

    try {
      setIsLoading(true);
      await productsAPI.create({
        ...formData,
        unitPrice: parseFloat(formData.unitPrice),
      });
      logger.log('✅ Produit créé');
      onComplete();
    } catch (err: any) {
      logger.error('❌ Erreur création produit:', err);
      setError(err.message || t('onboarding.product.createError'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div>
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-white mb-2">
          {t('onboarding.product.title')}
        </h1>
        <p className="text-white/60">
          {t('onboarding.product.subtitle')}
        </p>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        {/* Main card (2/3) */}
        <div className="lg:col-span-2">
          <div className="bg-white/10 backdrop-blur-xl rounded-3xl p-8 border border-white/20 shadow-2xl">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-xl bg-lime-500/20 flex items-center justify-center">
                <ShoppingBag size={20} className="text-lime-400" />
              </div>
              <h2 className="text-lg font-semibold text-white">
                {t('onboarding.product.productInfo')}
              </h2>
            </div>

            {error && (
              <div className="mb-6 p-4 bg-red-500/20 backdrop-blur-md border border-red-400/30 rounded-2xl text-white">
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-5">
              <GlassInput
                label={t('onboarding.product.name')}
                value={formData.name}
                onChange={(e) => handleChange('name', e.target.value)}
                onBlur={() => handleBlur('name')}
                error={getFieldError('name')}
                placeholder={t('onboarding.product.namePlaceholder')}
                required
              />

              <div>
                <label className="block text-sm font-medium text-white/80 mb-2">
                  {t('onboarding.product.description')}
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => handleChange('description', e.target.value)}
                  rows={3}
                  className="w-full px-4 py-3 bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-lime-400/50 focus:border-lime-400/50 transition-all duration-300 resize-none"
                  placeholder={t('onboarding.product.descriptionPlaceholder')}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <GlassInput
                  label={t('onboarding.product.unitPrice')}
                  type="number"
                  value={formData.unitPrice}
                  onChange={(e) => handleChange('unitPrice', e.target.value)}
                  onBlur={() => handleBlur('unitPrice')}
                  error={getFieldError('unitPrice')}
                  placeholder="0.00"
                  step={0.01}
                  min={0}
                  required
                />

                <GlassSelect
                  label={t('onboarding.product.unit')}
                  value={formData.unit}
                  onChange={(e) => handleChange('unit', e.target.value)}
                  options={[
                    { value: 'unité', label: t('onboarding.product.unitOptions.unite') },
                    { value: 'heure', label: t('onboarding.product.unitOptions.heure') },
                    { value: 'jour', label: t('onboarding.product.unitOptions.jour') },
                    { value: 'forfait', label: t('onboarding.product.unitOptions.forfait') },
                    { value: 'm²', label: t('onboarding.product.unitOptions.m2') },
                  ]}
                />

                <GlassSelect
                  label={t('onboarding.product.vat')}
                  value={formData.taxRateId}
                  onChange={(e) => handleChange('taxRateId', e.target.value)}
                  options={[
                    { value: '', label: isLoadingTaxRates ? t('onboarding.product.loadingVat') : t('onboarding.product.selectVat') },
                    ...taxRates.map((rate) => ({
                      value: rate.id,
                      label: `TVA ${Number(rate.rate).toFixed(0)}%`,
                    })),
                  ]}
                  required
                  disabled={isLoadingTaxRates}
                />
              </div>
            </form>
          </div>
        </div>

        {/* Sidebar (1/3) */}
        <div className="space-y-4">
          <div className="bg-white/10 backdrop-blur-xl rounded-3xl p-6 border border-white/20 shadow-2xl">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center">
                <Package size={16} className="text-lime-400" />
              </div>
              <h3 className="text-base font-semibold text-white">
                {t('onboarding.product.catalog')}
              </h3>
            </div>
            <p className="text-sm text-white/60">
              {t('onboarding.product.catalogInfo')}
            </p>
          </div>

          <div className="bg-white/5 backdrop-blur-md rounded-2xl p-4 border border-white/10">
            <p className="text-sm text-white/50">
              <span className="text-lime-400">{t('onboarding.product.tipLabel')}</span> {t('onboarding.product.tip')}
            </p>
          </div>

          <div className="space-y-3 pt-4">
            <GlassButton
              onClick={onSkip}
              disabled={isLoading}
              variant="ghost"
              className="w-full"
            >
              <SkipForward className="w-5 h-5" />
              {t('onboarding.product.skip')}
            </GlassButton>

            <GlassButton
              onClick={() => handleSubmit()}
              disabled={!formData.name.trim() || !formData.unitPrice || !formData.taxRateId || isLoading}
              isLoading={isLoading}
              className="w-full"
            >
              {t('onboarding.product.continue')}
              {!isLoading && <ArrowRight className="w-5 h-5" />}
            </GlassButton>
          </div>
        </div>
      </div>
    </div>
  );
};
